<script lang="ts">
    
    import { Map as Mapbox, Marker } from "mapbox-gl";
    import { createEventDispatcher, getContext, onDestroy, onMount } from "svelte";
    import type { CoordinatesDto, Fn, TargetDto } from "../../global";
    import type { Checkpoint } from "../../Model/Checkpoint";
    import { currentTarget } from "../../Services/game";
    import { hasPlayerPosition, playerPosition } from "../../Services/geolocation";

    export let checkpoint: Checkpoint;

    const dispatch = createEventDispatcher();
    const getMapbox = getContext('getMapbox') as () => Mapbox;
    const toggleTooFar: Fn = getContext('toggleTooFar');
    const toggleNeedGeolocation: Fn = getContext('toggleNeedGeolocation');

    let marker: Marker;
    let unsubIsVisible: Fn;
    let unsubIsAccesible: Fn;
    let unsubIsCompleted: Fn;
    let unsubPlayerPosition: Fn;
    let map: Mapbox;
    let lastKnownPlayerPosition: CoordinatesDto;

    $: updateTarget(marker, $currentTarget);

    onMount(() => {

        map = getMapbox();

        const element = document.createElement('div') as HTMLElement;
        const targetMark = document.createElement('div') as HTMLElement;
        const completedMark = document.createElement('div') as HTMLElement;
        const image = document.createElement('img') as HTMLImageElement;
        element.classList.add('checkpoint-marker');
        targetMark.classList.add('target-mark');
        completedMark.classList.add('completed-mark');
        element.style.setProperty('width', `${checkpoint.marker.width}px`);
        element.style.setProperty('height', `${checkpoint.marker.height}px`);
        image.classList.add('image');
        
        if (checkpoint.marker.cssClass) {
            image.classList.add(...checkpoint.marker.cssClass);
        }
        
        image.src = checkpoint.marker.image;
        element.addEventListener('click', onClick);
        element.append(targetMark, completedMark, image);

        unsubIsVisible = checkpoint.isVisible.subscribe(value => element.classList.toggle('hide', !value));
        unsubIsAccesible = checkpoint.isAccesible.subscribe(value => element.classList.toggle('inaccesible', !value));
        unsubIsCompleted = checkpoint.isCompleted.subscribe(value => completedMark.classList.toggle('show', value));

        marker = new Marker({
            element,
            anchor: 'bottom',
        });

        marker.setLngLat(checkpoint.position);
        marker.addTo(map);

        map.on('move', onMove);
        
        updateZIndex();
        setupLookAtPlayer();
    });

    onDestroy(() => {
        map?.off('move', onMove);
        marker?.remove();
        unsubIsVisible?.();
        unsubIsAccesible?.();
        unsubPlayerPosition?.();
        unsubIsCompleted?.();
    });

    function updateTarget(marker: Marker, target: TargetDto): void {
        if (!marker) {
            return;
        }
        marker.getElement().querySelector('.target-mark').classList.toggle('show', checkpoint.id === target?.checkpointId);
    }

    function onMove(): void {
        updateZIndex();
        updateLookAtPlayer();
    }

    function updateZIndex(): void {
        const lngLat = marker.getLngLat();
        const { y } = map.project(lngLat);
        marker.getElement().style.setProperty('z-index', `${Math.floor(y)}`);
    }

    function setupLookAtPlayer(): void {
        if (!checkpoint.lookAtPlayer || unsubPlayerPosition) {
            return;
        }
        unsubPlayerPosition = playerPosition.subscribe(playerPosition => {
            if (!playerPosition) {
                return;
            }
            lastKnownPlayerPosition = playerPosition;
            updateLookAtPlayer();
        });
    }

    function updateLookAtPlayer(): void {
        if (!lastKnownPlayerPosition || !checkpoint.lookAtPlayer || !map) {
            return;
        }
        const playerX = map.project(lastKnownPlayerPosition).x;
        const checkpointX = map.project(checkpoint.position).x;
        const image = marker.getElement().querySelector('.image') as HTMLImageElement;
        image.classList.toggle('invert', playerX > checkpointX);
    }

    function onClick(): void {
        if (checkpoint.needGeolocation && !hasPlayerPosition()) {
            toggleNeedGeolocation();
            return;
        }
        if (checkpoint.isTooFar()) {
           toggleTooFar();
            return;
        }
        if (!checkpoint.playerCanInteract()) {
            return;
        }
        dispatch('click', checkpoint);
    }

</script>

<style lang="scss">

    :global(.checkpoint-marker) {
        z-index: 2;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
    }

    :global(.checkpoint-marker .target-mark, .checkpoint-marker .completed-mark) {
        position: absolute;
        z-index: 2;
        width: 20px;
        height: 20px;
        background-size: 80% 80%;
        background-position: center;
        background-repeat: no-repeat;
        bottom: 100%;
        left: calc(50% - 10px);
        border-radius: 50%;
        opacity: 0;
        transform: translateY(30px);
        transition-property: opacity, transform;
        transition-duration: 400ms;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    :global(.checkpoint-marker .target-mark) {
        background-color: var(--color-primary);
        background-image: url('/images/target.svg');
    }

    :global(.checkpoint-marker .completed-mark) {
        background-color: #fff;
        border: 1px solid #7dc983;
        background-image: url('/images/completed.svg');
    }

    :global(.checkpoint-marker .target-mark.show, .checkpoint-marker .completed-mark.show) {
        opacity: 1;
        transform: translateY(0);
    }

    :global(.checkpoint-marker .image) {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    :global(.checkpoint-marker .image.invert) {
        transform: scaleX(-1);
    }

    :global(.checkpoint-marker.inaccesible) {
        filter: grayscale(1);
    }

    :global(.checkpoint-marker.hide) {
        opacity: 0;
        touch-action: none;
        pointer-events: none;
    }

</style>
